import type { DesignItem, ViewName } from './types';
import type { PrintAreaSize } from './itemPlacement';

export type AreaSizeMap = Record<string, PrintAreaSize>;

function finitePositive(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Parse authoring_area_sizes from template / designer_state metadata. */
export function readAuthoringAreaSizes(metadata: unknown): AreaSizeMap | undefined {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return undefined;
  const raw = (metadata as Record<string, unknown>).authoring_area_sizes;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: AreaSizeMap = {};
  for (const [view, size] of Object.entries(raw as Record<string, unknown>)) {
    if (!size || typeof size !== 'object' || Array.isArray(size)) continue;
    const width = finitePositive((size as Record<string, unknown>).width);
    const height = finitePositive((size as Record<string, unknown>).height);
    if (width && height) out[view] = { width, height };
  }
  return Object.keys(out).length ? out : undefined;
}

/** Natural, unrotated size of a layer as reported by whoever owns its geometry. */
export interface ItemMeasureSize {
  width: number;
  height: number;
}

export interface FitToPrintAreasOptions {
  /**
   * Share of the target area kept free on each side (0.02 = 2%).
   *
   * Strokes, shadows and anti-aliasing paint slightly outside the geometry the fit
   * measures, and the render canvas is exactly the print area, so a design scaled to
   * touch the edges loses those pixels.
   */
  inset?: number;
  /** Natural bounds from the layer's owning resolver (addon layouts measure larger than the box). */
  measure?: (item: DesignItem) => ItemMeasureSize | null;
  /**
   * Ask the layer's owning resolver to record a target box.
   *
   * Addon-owned layers (e.g. typography) re-run their own auto-fit from `fontSize` and
   * their frame, so scaling those re-flows the layout. Their resolver instead persists a
   * visual scale, which keeps the authored glyph placement intact.
   */
  fitToBox?: (item: DesignItem, box: PrintAreaSize) => Record<string, unknown> | null;
  /**
   * Per-view margins in target-area px (see `printSafetyMargins`). The design keeps at least
   * `x` / `y` free on each side when that is more than `inset`, so a fitted template stays
   * inside the product's safe area instead of tripping Preflight's edge warning. `bleedX` /
   * `bleedY` is how far a background reaches past each edge.
   */
  margins?: Record<string, AreaMargin>;
  /**
   * Authoring print areas (`authoring_area_sizes`). A layer whose bounds cover its whole
   * authoring area is a background: it is left out of the fit and stretched to cover the
   * target area plus its bleed, instead of shrinking with the rest of the design.
   */
  sourceAreas?: AreaSizeMap | null;
  /**
   * Widen a text layer's box to its measured content width (`measure`) before the fit, keeping
   * its centre. A box saved for another font or area otherwise loads narrower than its text,
   * which then wraps and is cut off. Only the width: one-line text does not wrap downwards.
   */
  growToContent?: boolean;
}

export interface AreaMargin {
  x: number;
  y: number;
  bleedX?: number;
  bleedY?: number;
}

/**
 * Safe-area margins of each view, in that view's print-area px, from the product's Preflight
 * profile and print size in cm. Mirrors Preflight's safe inset ((safety + bleed) mm, bleed only
 * outside `placed` mode; per-view overrides win) so layout and checks agree. Views without a
 * physical size get no margin.
 */
export function printSafetyMargins(
  areas: AreaSizeMap | null | undefined,
  physicalSizesCm: Record<string, { width: number; height: number }> | null | undefined,
  profile: Record<string, unknown> | null | undefined,
): Record<string, AreaMargin> {
  const out: Record<string, AreaMargin> = {};
  const base = profile && typeof profile === 'object' ? profile : {};
  const views = (base.views && typeof base.views === 'object' ? base.views : {}) as Record<string, Record<string, unknown>>;
  for (const [view, area] of Object.entries(areas ?? {})) {
    const cm = physicalSizesCm?.[view];
    const cmW = finitePositive(cm?.width);
    const cmH = finitePositive(cm?.height);
    if (!cmW || !cmH) continue;
    const merged = { ...base, ...(views[view] ?? {}) } as Record<string, unknown>;
    const mode = String(merged.mode || 'placed');
    const nonNegative = (value: unknown, fallback: number) => {
      const n = Number(value);
      return Number.isFinite(n) && n >= 0 ? n : fallback;
    };
    const safetyMm = nonNegative(merged.safetyMm, 3);
    const bleedMm = mode === 'placed' ? 0 : nonNegative(merged.bleedMm, 3);
    const px = (mm: number, sizeCm: number, sizePx: number) => (mm / 10 / sizeCm) * sizePx;
    out[view] = {
      // +1 px: Preflight compares item bounds with a strict `<`, and rounding moves edges by up to 1 px.
      x: px(safetyMm + bleedMm, cmW, area.width) + 1,
      y: px(safetyMm + bleedMm, cmH, area.height) + 1,
      bleedX: px(bleedMm, cmW, area.width) + (bleedMm > 0 ? 1 : 0),
      bleedY: px(bleedMm, cmH, area.height) + (bleedMm > 0 ? 1 : 0),
    };
  }
  return out;
}

/** Whether a layer's bounds cover the whole authoring area of its view (a background). */
function coversArea(item: DesignItem, area: PrintAreaSize | undefined, measure?: FitToPrintAreasOptions['measure']): boolean {
  if (!area) return false;
  const b = itemVisualBounds(item, measure);
  return b.minX <= 0.5 && b.minY <= 0.5 && b.maxX >= area.width - 0.5 && b.maxY >= area.height - 0.5;
}

/** Layers the print-area render actually draws: visible, and not placed on the product stage. */
function isPrintAreaLayer(item: DesignItem): boolean {
  const ex = item as Record<string, unknown>;
  return ex.visible !== false && ex.placementSpace !== 'product-stage';
}

/** Bounds of one layer after rotation, using the largest of its box and its measured art. */
function itemVisualBounds(
  item: DesignItem,
  measure?: FitToPrintAreasOptions['measure'],
): { minX: number; minY: number; maxX: number; maxY: number } {
  const left = Number(item.left) || 0;
  const top = Number(item.top) || 0;
  const boxWidth = Math.max(1, Number(item.width) || 1);
  const boxHeight = Math.max(1, Number(item.height) || 1);
  const measured = measure?.(item) ?? null;
  const width = Math.max(boxWidth, finitePositive(measured?.width));
  const height = Math.max(boxHeight, finitePositive(measured?.height));
  const centerX = left + boxWidth / 2;
  const centerY = top + boxHeight / 2;
  const radians = ((Number(item.angle) || 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const corners: Array<[number, number]> = [
    [centerX - width / 2, centerY - height / 2],
    [centerX + width / 2, centerY - height / 2],
    [centerX + width / 2, centerY + height / 2],
    [centerX - width / 2, centerY + height / 2],
  ];
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const [x, y] of corners) {
    const rotatedX = centerX + (x - centerX) * cos - (y - centerY) * sin;
    const rotatedY = centerY + (x - centerX) * sin + (y - centerY) * cos;
    minX = Math.min(minX, rotatedX);
    minY = Math.min(minY, rotatedY);
    maxX = Math.max(maxX, rotatedX);
    maxY = Math.max(maxY, rotatedY);
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Bounding box of everything the print-area render will paint, in print-area coordinates.
 *
 * Unlike `itemsBoundingBox` this accounts for rotation and for addon layouts whose art is
 * larger than the stored box, so a contain-fit against it cannot clip a layer.
 */
export function itemsVisualBoundingBox(
  items: DesignItem[],
  options: Pick<FitToPrintAreasOptions, 'measure'> = {},
): { minX: number; minY: number; width: number; height: number } | null {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const item of items) {
    const bounds = itemVisualBounds(item, options.measure);
    minX = Math.min(minX, bounds.minX);
    minY = Math.min(minY, bounds.minY);
    maxX = Math.max(maxX, bounds.maxX);
    maxY = Math.max(maxY, bounds.maxY);
  }
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
  return {
    minX,
    minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

/** Axis-aligned bounding box of items in print-area coordinates. */
export function itemsBoundingBox(items: DesignItem[]): PrintAreaSize | null {
  if (!items.length) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const item of items) {
    const left = Number(item.left) || 0;
    const top = Number(item.top) || 0;
    const width = Math.max(1, Number(item.width) || 1);
    const height = Math.max(1, Number(item.height) || 1);
    minX = Math.min(minX, left);
    minY = Math.min(minY, top);
    maxX = Math.max(maxX, left + width);
    maxY = Math.max(maxY, top + height);
  }
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
  return {
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

function scaleTypographyFrame(item: DesignItem, scale: number): DesignItem {
  const frame = (item as Record<string, unknown>).typographyFrame;
  if (!frame || typeof frame !== 'object' || Array.isArray(frame)) return item;
  const record = frame as Record<string, unknown>;
  const width = finitePositive(record.width);
  const height = finitePositive(record.height);
  if (!width && !height) return item;
  return {
    ...item,
    typographyFrame: {
      ...record,
      ...(width ? { width: Math.max(1, Math.round(width * scale)) } : {}),
      ...(height ? { height: Math.max(1, Math.round(height * scale)) } : {}),
    },
  } as DesignItem;
}

/** Scale a single item's geometry (and fontSize / typographyFrame) by a uniform factor, then offset. */
export function scaleItemToArea<T extends DesignItem>(
  item: T,
  scale: number,
  offsetX: number,
  offsetY: number,
): T {
  const next = {
    ...item,
    left: Math.round((Number(item.left) || 0) * scale + offsetX),
    top: Math.round((Number(item.top) || 0) * scale + offsetY),
    width: Math.max(1, Math.round((Number(item.width) || 1) * scale)),
    height: Math.max(1, Math.round((Number(item.height) || 1) * scale)),
  } as T;

  if (item.type === 'text' && item.fontSize != null) {
    const fontSize = Number(item.fontSize);
    if (Number.isFinite(fontSize) && fontSize > 0) {
      (next as DesignItem).fontSize = Math.max(1, Math.round(fontSize * scale));
    }
    // Tracking and outline are px too: left as is they widen a shrunk line past its box.
    const ex = next as Record<string, unknown>;
    for (const key of ['letterSpacing', 'outlineWidth'] as const) {
      const value = Number((item as Record<string, unknown>)[key]);
      if (Number.isFinite(value) && value !== 0) ex[key] = Math.round(value * scale * 10) / 10;
    }
  }

  return scaleTypographyFrame(next, scale) as T;
}

/**
 * Contain-scale design items from authoring print-area sizes into target print areas.
 * When a view has no source size in `sourceAreas`, falls back to the items' bounding box.
 * Identity when source and target match (or either side is missing/invalid).
 */
export function scaleDesignItemsToPrintAreas<T extends DesignItem>(
  items: Partial<Record<ViewName, T[]>> | Record<string, T[]>,
  sourceAreas: AreaSizeMap | null | undefined,
  targetAreas: AreaSizeMap | null | undefined,
): Partial<Record<ViewName, T[]>> {
  const out: Partial<Record<ViewName, T[]>> = {};
  const targets = targetAreas ?? {};
  const sources = sourceAreas ?? {};

  for (const [view, list] of Object.entries(items ?? {})) {
    if (!Array.isArray(list)) {
      out[view as ViewName] = list;
      continue;
    }
    if (!list.length) {
      out[view as ViewName] = [];
      continue;
    }

    const target = targets[view];
    const targetW = finitePositive(target?.width);
    const targetH = finitePositive(target?.height);
    if (!targetW || !targetH) {
      out[view as ViewName] = list.map((item) => ({ ...item }));
      continue;
    }

    const authored = sources[view];
    const authoredW = finitePositive(authored?.width);
    const authoredH = finitePositive(authored?.height);
    const bbox = (!authoredW || !authoredH) ? itemsBoundingBox(list) : null;
    const sourceW = authoredW || finitePositive(bbox?.width);
    const sourceH = authoredH || finitePositive(bbox?.height);

    if (!sourceW || !sourceH) {
      out[view as ViewName] = list.map((item) => ({ ...item }));
      continue;
    }

    // Same authored area → no remap. Skipped for the bbox fallback: a bbox that happens to
    // match the area still needs the origin shift below to end up centred.
    if (
      authoredW && authoredH
      && Math.abs(sourceW - targetW) < 0.5 && Math.abs(sourceH - targetH) < 0.5
    ) {
      out[view as ViewName] = list.map((item) => ({ ...item }));
      continue;
    }

    const scale = Math.min(targetW / sourceW, targetH / sourceH);
    const offsetX = (targetW - sourceW * scale) / 2;
    const offsetY = (targetH - sourceH * scale) / 2;

    // Bbox fallback: items may not start at (0,0); shift so the bbox origin maps through scale+center.
    let originX = 0;
    let originY = 0;
    if (!authoredW || !authoredH) {
      let minX = Number.POSITIVE_INFINITY;
      let minY = Number.POSITIVE_INFINITY;
      for (const item of list) {
        minX = Math.min(minX, Number(item.left) || 0);
        minY = Math.min(minY, Number(item.top) || 0);
      }
      if (Number.isFinite(minX) && Number.isFinite(minY)) {
        originX = minX;
        originY = minY;
      }
    }

    out[view as ViewName] = list.map((item) => {
      const shifted = {
        ...item,
        left: (Number(item.left) || 0) - originX,
        top: (Number(item.top) || 0) - originY,
      } as T;
      return scaleItemToArea(shifted, scale, offsetX, offsetY);
    });
  }

  return out;
}

/** A text layer with its box widened (centred) to the measured content width, when that is larger. */
function growTextToContent<T extends DesignItem>(item: T, measure: NonNullable<FitToPrintAreasOptions['measure']>): T {
  if (item.type !== 'text') return item;
  const width = Math.max(1, Number(item.width) || 1);
  const nextW = Math.max(width, finitePositive(measure(item)?.width));
  if (nextW === width) return item;
  return { ...item, left: (Number(item.left) || 0) - (nextW - width) / 2, width: nextW } as T;
}

/** Default share of the print area kept free on each side by `fitDesignItemsToPrintAreas`. */
export const FIT_AREA_INSET = 0.02;

/**
 * Contain-fit each view's design into its target print area, centred.
 *
 * Unlike `scaleDesignItemsToPrintAreas`, this ignores the authoring area entirely, so the
 * design fills the target area instead of keeping the share of the area it was authored at.
 * Used for the generated product mockup, where the design has to read at thumbnail size.
 *
 * The scale comes from the limiting axis of the rotated, measured bounds of every layer the
 * print-area render paints, so no layer can end up outside the area and be clipped. Layers
 * drawn on the product stage, and hidden layers, keep their geometry: the print-area render
 * skips them, so scaling them would only move artwork the mockup never shows.
 */
export function fitDesignItemsToPrintAreas<T extends DesignItem>(
  items: Partial<Record<ViewName, T[]>> | Record<string, T[]>,
  targetAreas: AreaSizeMap | null | undefined,
  options: FitToPrintAreasOptions = {},
): Partial<Record<ViewName, T[]>> {
  const out: Partial<Record<ViewName, T[]>> = {};
  const targets = targetAreas ?? {};
  const inset = Math.min(0.45, Math.max(0, Number(options.inset ?? FIT_AREA_INSET) || 0));

  for (const [view, entries] of Object.entries(items ?? {})) {
    if (!Array.isArray(entries)) {
      out[view as ViewName] = entries;
      continue;
    }
    let list = entries;

    if (options.growToContent && options.measure) list = list.map((item) => growTextToContent(item, options.measure!));

    const target = targets[view];
    const targetW = finitePositive(target?.width);
    const targetH = finitePositive(target?.height);
    const source = options.sourceAreas?.[view];
    const isBackground = (item: T) => isPrintAreaLayer(item) && coversArea(item, source, options.measure);
    const fittable = list.filter((item) => isPrintAreaLayer(item) && !isBackground(item));
    const bounds = targetW && targetH ? itemsVisualBoundingBox(fittable, options) : null;
    const margin = options.margins?.[view];
    const insetX = Math.max(targetW * inset, finitePositive(margin?.x));
    const insetY = Math.max(targetH * inset, finitePositive(margin?.y));

    const availableW = Math.max(1, targetW - insetX * 2);
    const availableH = Math.max(1, targetH - insetY * 2);
    const scale = bounds ? Math.min(availableW / bounds.width, availableH / bounds.height) : 1;
    const offsetX = bounds ? insetX + (availableW - bounds.width * scale) / 2 : 0;
    const offsetY = bounds ? insetY + (availableH - bounds.height * scale) / 2 : 0;

    out[view as ViewName] = list.map((item) => {
      if (!isPrintAreaLayer(item)) return { ...item };
      if (targetW && targetH && isBackground(item)) {
        // Cover the target area plus its bleed, keeping the aspect, centred.
        const b = itemVisualBounds(item, options.measure);
        const w = Math.max(1, b.maxX - b.minX);
        const h = Math.max(1, b.maxY - b.minY);
        const cover = Math.max(
          (targetW + 2 * finitePositive(margin?.bleedX)) / w,
          (targetH + 2 * finitePositive(margin?.bleedY)) / h,
        );
        const shiftedBg = { ...item, left: (Number(item.left) || 0) - b.minX, top: (Number(item.top) || 0) - b.minY } as T;
        return scaleItemToArea(shiftedBg, cover, (targetW - w * cover) / 2, (targetH - h * cover) / 2);
      }
      if (!bounds) return { ...item };
      const shifted = {
        ...item,
        left: (Number(item.left) || 0) - bounds.minX,
        top: (Number(item.top) || 0) - bounds.minY,
      } as T;

      const box = {
        width: Math.max(1, Math.round((Number(item.width) || 1) * scale)),
        height: Math.max(1, Math.round((Number(item.height) || 1) * scale)),
      };
      const patch = options.fitToBox?.(item, box) ?? null;
      if (patch) {
        return {
          ...shifted,
          left: Math.round((Number(shifted.left) || 0) * scale + offsetX),
          top: Math.round((Number(shifted.top) || 0) * scale + offsetY),
          ...box,
          ...patch,
        } as T;
      }

      return scaleItemToArea(shifted, scale, offsetX, offsetY);
    });
  }
  return out;
}
